import { useCallback } from 'react';

import { useCreditPacks } from './useCreditPacks';
import { useSubscription } from './useSubscription';

import type { FeatureKey, PlanLimits, PlanWarning, UsageSummary, AccessCheckResult } from '../types/billing';

// ----------------------------------------------------------------------

const BOOLEAN_FEATURE_MAP: Record<string, keyof PlanLimits> = {
  ai_interviews: 'hasAiInterviews',
  custom_branding: 'hasCustomBranding',
  remove_watermark: 'hasRemoveWatermark',
  automations: 'hasAutomations',
  reports: 'hasReports',
};

const NUMERIC_FEATURE_MAP: Record<string, { limitKey: keyof PlanLimits; usageKey: keyof UsageSummary }> = {
  candidates: { limitKey: 'maxCandidates', usageKey: 'candidates' },
  screenings: { limitKey: 'maxScreenings', usageKey: 'screenings' },
  interviews: { limitKey: 'maxInterviews', usageKey: 'interviews' },
  coding_rounds: { limitKey: 'maxCodingRounds', usageKey: 'codingRounds' },
  job_posts: { limitKey: 'maxJobPosts', usageKey: 'jobPosts' },
};

// Human-readable labels for each feature key
export const FEATURE_LABELS: Record<FeatureKey, string> = {
  candidates: 'Candidates',
  screenings: 'AI Screenings',
  interviews: 'Core Interviews',
  coding_rounds: 'Coding Rounds',
  job_posts: 'Job Posts',
  ai_interviews: 'Core Interviews',
  custom_branding: 'Custom Branding',
  remove_watermark: 'Remove Watermark',
  automations: 'Automations',
  reports: 'Reports',
};

// ----------------------------------------------------------------------

export type UsePlanAccessReturn = {
  planLimits: PlanLimits | null;
  currentUsage: UsageSummary | null;
  warnings: PlanWarning[] | null;
  isFeatureAvailable: (feature: FeatureKey) => boolean;
  canPerformAction: (feature: FeatureKey) => AccessCheckResult;
  isApproachingLimit: (feature: FeatureKey) => boolean;
  getUsage: (feature: FeatureKey) => { used: number; limit: number };
  isLoading: boolean;

  // ── Phase 4 additions ────────────────────────────────────────────────────
  /** Plan remaining + credit remaining combined for any numeric feature. */
  effectiveRemaining: (feature: string) => number;
  /** Same as canPerformAction but consults credits as fallback. */
  canPerformActionConsideringCredits: (feature: string) => {
    allowed: boolean;
    remaining: number;
    fromCredits: boolean;
  };
  /** True if plan is exhausted but credits still exist for this feature. */
  isOnCreditOverflow: (feature: string) => boolean;
};

// ----------------------------------------------------------------------

export function usePlanAccess(): UsePlanAccessReturn {
  const { planLimits, currentUsage, warnings, isDevAccount, isLoading: isSubscriptionLoading } = useSubscription();
  const { balanceFor } = useCreditPacks();

  const isFeatureAvailable = useCallback(
    (feature: FeatureKey): boolean => {
      if (isDevAccount) return true;
      if (!planLimits) return true;
      const boolKey = BOOLEAN_FEATURE_MAP[feature];
      if (!boolKey) return true; // numeric features are always "available" — just limited
      return Boolean(planLimits[boolKey]);
    },
    [planLimits, isDevAccount]
  );

  const canPerformAction = useCallback(
    (feature: FeatureKey): AccessCheckResult => {
      if (isDevAccount) {
        const mapping = NUMERIC_FEATURE_MAP[feature];
        const used = mapping && currentUsage ? Number(currentUsage[mapping.usageKey]) : 0;
        return { allowed: true, limit: 0, used, remaining: Infinity, percentage: 0, isApproachingLimit: false };
      }

      // Loading-window fix (Phase 2 follow-up I.4): return permissive defaults while data
      // is in-flight so components don't render "0 remaining" or block actions pre-data.
      if (isSubscriptionLoading) {
        return { allowed: true, limit: 0, used: 0, remaining: Infinity, percentage: 0, isApproachingLimit: false };
      }

      const mapping = NUMERIC_FEATURE_MAP[feature];
      if (!mapping || !planLimits || !currentUsage) {
        return { allowed: true, limit: 0, used: 0, remaining: 0, percentage: 0, isApproachingLimit: false };
      }

      const limit = Number(planLimits[mapping.limitKey]);
      const used = Number(currentUsage[mapping.usageKey]);

      // A limit of 0 (or NaN) means unlimited — always allow
      if (!limit || limit <= 0) {
        return { allowed: true, limit: 0, used, remaining: Infinity, percentage: 0, isApproachingLimit: false };
      }

      const remaining = Math.max(0, limit - used);
      const percentage = Math.round((used / limit) * 100);
      const allowed = used < limit;
      const approaching = percentage >= 80 && allowed;

      return {
        allowed,
        limit,
        used,
        remaining,
        percentage,
        isApproachingLimit: approaching,
        warningMessage: approaching
          ? `You've used ${percentage}% of your ${FEATURE_LABELS[feature].toLowerCase()} limit`
          : undefined,
      };
    },
    [planLimits, currentUsage, isDevAccount, isSubscriptionLoading]
  );

  const isApproachingLimit = useCallback(
    (feature: FeatureKey): boolean => canPerformAction(feature).isApproachingLimit,
    [canPerformAction]
  );

  const getUsage = useCallback(
    (feature: FeatureKey): { used: number; limit: number } => {
      const mapping = NUMERIC_FEATURE_MAP[feature];
      if (!mapping || !planLimits || !currentUsage) return { used: 0, limit: 0 };
      return {
        used: Number(currentUsage[mapping.usageKey]),
        limit: Number(planLimits[mapping.limitKey]),
      };
    },
    [planLimits, currentUsage]
  );

  // ── Phase 4: credit-aware derivations ──────────────────────────────────

  const effectiveRemaining = useCallback(
    (feature: string): number => {
      if (isDevAccount) return Infinity;
      const mapping = NUMERIC_FEATURE_MAP[feature];
      if (!mapping || !planLimits || !currentUsage) return 0;
      const planMax = Number(planLimits[mapping.limitKey]);
      const planUsed = Number(currentUsage[mapping.usageKey]);
      if (!planMax || planMax <= 0) return Infinity;
      const planRemaining = Math.max(0, planMax - planUsed);
      const credits = balanceFor(feature)?.remaining ?? 0;
      return planRemaining + credits;
    },
    [planLimits, currentUsage, isDevAccount, balanceFor]
  );

  const canPerformActionConsideringCredits = useCallback(
    (feature: string): { allowed: boolean; remaining: number; fromCredits: boolean } => {
      // CRITICAL: short-circuit during loading so actions aren't false-blocked on pre-data
      // render (same loading-window fix applied to credit-aware path).
      if (isSubscriptionLoading) {
        return { allowed: true, remaining: 0, fromCredits: false };
      }
      if (isDevAccount) {
        return { allowed: true, remaining: Infinity, fromCredits: false };
      }
      const mapping = NUMERIC_FEATURE_MAP[feature];
      if (!mapping || !planLimits || !currentUsage) {
        return { allowed: true, remaining: 0, fromCredits: false };
      }
      const planMax = Number(planLimits[mapping.limitKey]);
      const planUsed = Number(currentUsage[mapping.usageKey]);
      if (!planMax || planMax <= 0) {
        return { allowed: true, remaining: Infinity, fromCredits: false };
      }
      const planRemaining = Math.max(0, planMax - planUsed);
      const credits = balanceFor(feature)?.remaining ?? 0;
      const total = planRemaining + credits;
      return {
        allowed: total > 0,
        remaining: total,
        fromCredits: planRemaining === 0 && credits > 0,
      };
    },
    [planLimits, currentUsage, isDevAccount, isSubscriptionLoading, balanceFor]
  );

  const isOnCreditOverflow = useCallback(
    (feature: string): boolean => {
      const mapping = NUMERIC_FEATURE_MAP[feature];
      if (!mapping || !planLimits || !currentUsage) return false;
      const planMax = Number(planLimits[mapping.limitKey]);
      const planUsed = Number(currentUsage[mapping.usageKey]);
      const credits = balanceFor(feature)?.remaining ?? 0;
      return planUsed >= planMax && credits > 0;
    },
    [planLimits, currentUsage, balanceFor]
  );

  const isLoading = isSubscriptionLoading;

  return {
    planLimits,
    currentUsage,
    warnings,
    isFeatureAvailable,
    canPerformAction,
    isApproachingLimit,
    getUsage,
    isLoading,
    effectiveRemaining,
    canPerformActionConsideringCredits,
    isOnCreditOverflow,
  };
}
